/**
 * Box 3: Result and Cost per Result for the one selected objective, on the
 * cut window (spec §3.3, §4). Labelled by the metric it counts, always: "Result
 * (Impressions)", "Cost per Result (ต้นทุน / 1,000 impressions)" (rules-04 E3); the target
 * line is /looker's own TargetLine (the verdict's status word, never
 * re-derived); the coverage chips; and the footnotes box 9 shares: the
 * "ตัด 3 วันล่าสุด" chip and the DEC3 definition lines. Cost per Result fades
 * with "n<500" when box 9's row for the objective does (rules-04 D1).
 *
 * @module components/overview/ResultCards
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TONE_CLASS, deltaTone } from '@/services/looker/lookerDelta';
import { TargetLine } from '@/components/looker/TargetCells';
import { coverageLabelsFrom } from '@/services/looker/lookerCoverageLabels';
import type { ResultMappingInfo } from '@/services/targets/resultMetric';
import type { ResultCard } from '@/services/overview/overviewView';
import type { OverviewWindows } from '@/services/overview/overviewTypes';
import { costPerResultLabel, fmtDelta, fmtMoney, fmtResult, resultLabel } from '@/services/overview/overviewFormat';
import { Chip } from './OverviewTable';

interface FigureProps {
  id: string; label: string; value: string; raw: number | null; delta: number | null; tone: 'good' | 'bad' | 'neutral';
  /** R17, as box 2's cards: faded with "n<500" (rules-04 D1); a ratio only, never the Result count. */
  lowSample?: boolean;
}

const Figure: React.FC<FigureProps> = ({ id, label, value, raw, delta, tone, lowSample = false }) => {
  const { t } = useTranslation();
  return (
    <div data-metric={id} className="min-w-0 rounded-xl border border-neutral-200 p-3">
      <dt className="text-xs font-medium text-neutral-700">{label}</dt>
      <dd className={`mt-1 text-2xl font-semibold tabular-nums ${lowSample ? 'italic text-neutral-700' : 'text-ink-950'}`}>
        <span data-value={raw === null ? '' : String(raw)}>{value}</span>
        {lowSample && <span className="ml-1 text-xs font-normal">{t('overview.lowSample')}</span>}
      </dd>
      <dd data-delta={delta === null ? '' : String(delta)} className={`mt-1 text-xs font-semibold ${TONE_CLASS[tone]}`}>{fmtDelta(delta)}</dd>
    </div>
  );
};

/** The cut chip and date, and the DEC3 lines: failed / Reach badge + caveat / central. Shared with box 9. */
export const ResultFootnotes: React.FC<{ windows: OverviewWindows; mapping: ResultMappingInfo; awarenessShown: boolean; card: boolean }> = ({ windows, mapping, awarenessShown, card }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-3 space-y-1 text-xs">
      {windows.cutApplied && (
        <p className="text-neutral-700">
          <span className="mr-2 inline-block rounded-full border border-neutral-400 px-1.5 text-[11px] text-neutral-800">{t('overview.cutChip')}</span>
          {t('looker.resultThrough', { date: windows.through })}
        </p>
      )}
      {mapping.failed && <p className="font-medium text-error-700">{t('looker.resultMapping.failed')}</p>}
      {!mapping.failed && mapping.awareness === 'reach' && awarenessShown && (
        <>
          {card && mapping.overridden && <p className="font-medium text-ink-900">{t('looker.resultMapping.badge')}</p>}
          <p className="text-warning-700">{t('looker.resultMapping.caveat')}</p>
        </>
      )}
      {mapping.central && <p className="text-neutral-700">{t('looker.resultMapping.central')}</p>}
    </div>
  );
};

export const ResultCards: React.FC<{ card: ResultCard; windows: OverviewWindows; mapping: ResultMappingInfo; currency: string | null }> = ({ card, windows, mapping, currency }) => {
  const { t } = useTranslation();
  const notes = coverageLabelsFrom(t);
  const result = fmtResult(card, t);
  return (
    <div>
      <dl className="grid gap-3 sm:grid-cols-2">
        <Figure id="result" label={resultLabel(card.metric, t)} value={result} raw={card.result}
          delta={card.resultDelta} tone={deltaTone('result', card.resultDelta)} />
        <Figure id="costPerResult" label={costPerResultLabel(card.metric, t)} value={fmtMoney(card.costPerResult, currency)}
          raw={card.costPerResult} delta={card.costPerResultDelta} tone={deltaTone('cost_per_result', card.costPerResultDelta)} lowSample={card.lowSample} />
      </dl>
      {card.gaps.length > 0 && <p className="mt-2">{card.gaps.map((g) => <Chip key={g.platform} text={notes.notReported(g.platform, g.metric)} />)}</p>}
      <div className="mt-2">
        {card.targets === null
          ? <p className="text-sm text-neutral-700">{t('overview.needsWorkspace')}</p>
          : <TargetLine tile={{ targets: card.targets }} />}
      </div>
      <ResultFootnotes windows={windows} mapping={mapping} awarenessShown={card.objective === 'awareness'} card />
    </div>
  );
};
